// CorporateClients.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
    BadgeIndianRupee,
    BriefcaseBusiness,
    Building2,
    CheckCircle2,
    CircleAlert,
    Contact,
    FileText,
    Loader2,
    MapPin,
    RotateCcw,
    Send,
    UploadCloud,
    X
} from 'lucide-react';
import { confirmDialog, toast as notify } from '../../../components/notifications/notificationService';
import { isAuthenticated } from '../../../utils/auth';
import Sidebar from '../../../components/Sidebar/sidebar';
import Header from '../../../components/Header';
import './CorporateClients.css';
import axios from 'axios';
import api from '../../../utils/api';
import { API_BASE_URL } from '../../../utils/apiBase';

const initialFormData = {
    autoGeneratedId: '',
    customerType: 'Corporate',
    contactPersonName: '',
    contactNo: '',
    companyName: '',
    email: '',
    pinCode: '',
    state: '',
    city: '',
    address: '',
    income: '',
    gst: '',
    pan: '',
    note: ''
};

const Client = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [pinStatus, setPinStatus] = useState('');
    const [documents, setDocuments] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [formData, setFormData] = useState(initialFormData);
    const fieldRefs = useRef({});
    const fileInputRef = useRef(null);

    const validators = useMemo(() => ({
        contactPersonName: (v) =>
            !v.trim()
                ? 'Contact person name is required'
                : v.trim().length < 3
                    ? 'Must be at least 3 characters'
                    : '',

        contactNo: (v) =>
            /^[6-9]\d{9}$/.test(v)
                ? ''
                : 'Enter a valid 10-digit mobile number',

        companyName: (v) =>
            v && (!/[A-Za-z]/.test(v) || v.trim().length < 3)
                ? 'Enter a valid company name'
                : '',

        email: (v) =>
            /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim())
                ? ''
                : 'Enter a valid email address',

        pinCode: (v) =>
            /^[1-9][0-9]{5}$/.test(v)
                ? ''
                : 'Enter a valid 6-digit PIN code',

        address: (v) =>
            !v.trim()
                ? 'Address is required'
                : v.trim().length < 10
                    ? 'Address looks too short'
                    : '',

        income: (v) =>
            v && (Number.isNaN(Number(v)) || Number(v) <= 0)
                ? 'Income must be a positive number'
                : '',

        gst: (v) =>
            v &&
                !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
                    v.toUpperCase()
                )
                ? 'Enter a valid GST number'
                : '',

        pan: (v) =>
            v &&
                !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(v.toUpperCase())
                ? 'Enter a valid PAN number'
                : '',

        note: (v) =>
            v.length > 500 ? 'Maximum 500 characters allowed' : ''
    }), []);

    const requiredFields = useMemo(
        () => ['contactPersonName', 'contactNo', 'email', 'pinCode', 'state', 'city', 'address'],
        []
    );

    const completedRequiredCount = requiredFields.filter((field) => {
        const value = String(formData[field] || '').trim();
        const fieldError = validators[field] ? validators[field](value) : '';
        return value && !fieldError;
    }).length;
    const progressPercent = Math.round((completedRequiredCount / requiredFields.length) * 100);

    useEffect(() => {
        const timer = window.setTimeout(() => setPageLoading(false), 350);
        return () => window.clearTimeout(timer);
    }, []);

    const fetchAutoGeneratedId = useCallback(async () => {
        try {
            const response = await api.get(`${API_BASE_URL}/api/corporate-clients/next-client-id`);
            setFormData((prev) => ({
                ...prev,
                autoGeneratedId: response.data.autoGeneratedId
            }));
            setErrors((prev) => ({ ...prev, autoGeneratedId: '' }));
        } catch (error) {
            console.error('Failed to fetch Corporate Client ID:', error);
            setErrors((prev) => ({
                ...prev,
                autoGeneratedId: 'Failed to generate Corporate Client ID.'
            }));
        }
    }, []);

    useEffect(() => {
        fetchAutoGeneratedId();
    }, [fetchAutoGeneratedId]);

    if (!isAuthenticated()) return <Navigate to="/" />;

    const showToast = (type, message) => {
        (notify[type] || notify.info)(message);
    };

    const validateField = (name, value) => {
        if (validators[name]) return validators[name](value || '');
        if (name === 'state') return value ? '' : 'State is required';
        if (name === 'city') return value ? '' : 'City is required';
        return '';
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const normalizedValue =
            name === 'gst' || name === 'pan' ? value.toUpperCase() : value;

        setFormData((prev) => ({
            ...prev,
            [name]: normalizedValue
        }));

        if (touched[name] || validators[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: validateField(name, normalizedValue)
            }));
        }

        if (name === 'pinCode') {
            setPinStatus('');
            setFormData((prev) => ({
                ...prev,
                state: normalizedValue.length === 6 ? prev.state : '',
                city: normalizedValue.length === 6 ? prev.city : ''
            }));

            if (normalizedValue.length === 6 && /^[1-9][0-9]{5}$/.test(normalizedValue)) {
                fetchStateCity(normalizedValue);
            }
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouched((prev) => ({ ...prev, [name]: true }));
        setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    };

    const focusNextField = (currentName) => {
        const fieldOrder = [
            'companyName',
            'contactPersonName',
            'email',
            'contactNo',
            'pan',
            'gst',
            'income',
            'pinCode',
            'address',
            'note'
        ];
        const nextName = fieldOrder[fieldOrder.indexOf(currentName) + 1];
        fieldRefs.current[nextName]?.focus();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
            e.preventDefault();
            focusNextField(e.target.name);
        }
    };

    const fetchStateCity = async (pin) => {
        setPinStatus('fetching');
        try {
            const res = await axios.get(`https://api.postalpincode.in/pincode/${pin}`);

            if (res.data?.[0]?.Status === 'Success') {
                const postData = res.data[0].PostOffice[0];

                setFormData((prev) => ({
                    ...prev,
                    state: postData.State || '',
                    city: postData.District || ''
                }));

                setErrors((prev) => ({ ...prev, pinCode: '', state: '', city: '' }));
                setPinStatus('success');
            } else {
                setErrors((prev) => ({ ...prev, pinCode: 'Invalid PIN code' }));
                setPinStatus('error');
            }
        } catch (error) {
            console.error(error);
            setErrors((prev) => ({ ...prev, pinCode: 'Unable to fetch location details' }));
            setPinStatus('error');
        }
    };

    const validate = () => {
        const temp = {};

        Object.keys(validators).forEach((key) => {
            temp[key] = validators[key](formData[key] || '');
        });

        temp.state = formData.state ? '' : 'State is required';
        temp.city = formData.city ? '' : 'City is required';

        setErrors(temp);
        setTouched(Object.keys(temp).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
        return Object.values(temp).every((x) => x === '');
    };

    const handleReset = () => {
        setFormData((prev) => ({
            ...initialFormData,
            autoGeneratedId: prev.autoGeneratedId
        }));
        setErrors({});
        setTouched({});
        setPinStatus('');
        setDocuments([]);
        setUploadProgress(0);
        showToast('success', 'Form reset successfully.');
    };

    const handleDocumentSelection = (files) => {
        const selectedFiles = Array.from(files || []);
        if (!selectedFiles.length) return;

        setDocuments((prev) => [
            ...prev,
            ...selectedFiles.map((file) => ({
                id: `${file.name}-${file.lastModified}`,
                name: file.name,
                size: file.size,
                type: file.type || 'Document'
            }))
        ]);

        setUploadProgress(20);
        window.setTimeout(() => setUploadProgress(65), 180);
        window.setTimeout(() => setUploadProgress(100), 420);
        showToast('success', `${selectedFiles.length} document${selectedFiles.length > 1 ? 's' : ''} staged.`);
    };

    const removeDocument = (id) => {
        setDocuments((prev) => prev.filter((doc) => doc.id !== id));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            showToast('error', 'Please fix the highlighted fields.');
            return;
        }

        const confirmed = await confirmDialog({ title: 'Save corporate client?', message: 'Please confirm the details before creating this client record.', confirmText: 'Save Client', cancelText: 'Review Again', tone: 'info' });
        if (!confirmed) return;

        setLoading(true);
        try {
            const response = await api.post(`${API_BASE_URL}/api/corporate-clients/add`, formData);
            showToast('success', `Corporate client saved successfully. Client ID: ${response.data.autoGeneratedId}`);

            setFormData(initialFormData);
            setErrors({});
            setTouched({});
            setPinStatus('');
            setDocuments([]);
            setUploadProgress(0);
            await fetchAutoGeneratedId();
        } catch (error) {
            console.error(error);
            const serverErrors = error.response?.data?.errors;
            if (serverErrors) setErrors(serverErrors);
            showToast('error', error.response?.data?.error || 'Error saving corporate client.');
        } finally {
            setLoading(false);
        }
    };

    const renderField = ({
        name,
        label,
        type = 'text',
        placeholder,
        required = false,
        readOnly = false,
        multiline = false,
        className = ''
    }) => {
        const fieldError = touched[name] || errors[name] ? errors[name] : '';
        const InputTag = multiline ? 'textarea' : 'input';

        return (
            <div className={`ccf-field ${className} ${fieldError ? 'has-error' : ''}`}>
                <label htmlFor={name}>
                    {label}
                    {required && <span>*</span>}
                </label>
                <div className="ccf-input-shell">
                    <InputTag
                        id={name}
                        ref={(element) => {
                            fieldRefs.current[name] = element;
                        }}
                        type={multiline ? undefined : type}
                        name={name}
                        value={formData[name]}
                        placeholder={placeholder}
                        readOnly={readOnly}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                    />
                </div>
                {fieldError && (
                    <span className="ccf-error">
                        <CircleAlert size={14} />
                        {fieldError}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="client-container corporate-client-shell">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>

            <div className={`client-content corporate-client-content ${collapsed ? 'collapsed' : ''}`}>
                <Header />

                <main className="ccf-page">
                    {pageLoading ? (
                        <div className="ccf-skeleton-panel">
                            <div className="ccf-skeleton ccf-skeleton-title" />
                            <div className="ccf-skeleton-grid">
                                {Array.from({ length: 8 }).map((_, index) => (
                                    <div className="ccf-skeleton-card" key={index} />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <>
                            <section className="ccf-hero">
                                <div>
                                    <span className="ccf-eyebrow">Client Management</span>
                                    <h1>Corporate Client Intake</h1>
                                    <p>Capture company, contact, compliance, and location details in one guided workspace.</p>
                                </div>
                                <div className="ccf-progress-card" aria-label="Required field completion">
                                    <span>{progressPercent}% Complete</span>
                                    <div className="ccf-progress-track">
                                        <div style={{ width: `${progressPercent}%` }} />
                                    </div>
                                    <small>{completedRequiredCount} of {requiredFields.length} required fields ready</small>
                                </div>
                            </section>

                            <form className="ccf-form" onSubmit={handleSubmit}>
                                <section className="ccf-card ccf-fade-in">
                                    <div className="ccf-section-heading">
                                        <span><Building2 size={20} /></span>
                                        <div>
                                            <h2>Company Details</h2>
                                            <p>Core corporate profile and client classification.</p>
                                        </div>
                                    </div>
                                    <div className="ccf-grid">
                                        {renderField({
                                            name: 'autoGeneratedId',
                                            label: 'Client ID',
                                            placeholder: 'Auto-generated',
                                            readOnly: true
                                        })}
                                        <div className="ccf-field">
                                            <label htmlFor="customerType">Client Type</label>
                                            <div className="ccf-input-shell ccf-locked-field">
                                                <input
                                                    id="customerType"
                                                    type="text"
                                                    name="customerType"
                                                    value={formData.customerType}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        {renderField({
                                            name: 'companyName',
                                            label: 'Company Name',
                                            placeholder: 'Acme Insurance Services Pvt Ltd'
                                        })}
                                    </div>
                                </section>

                                <section className="ccf-card ccf-fade-in delay-1">
                                    <div className="ccf-section-heading">
                                        <span><Contact size={20} /></span>
                                        <div>
                                            <h2>Contact Person Details</h2>
                                            <p>Primary communication point for policy and service updates.</p>
                                        </div>
                                    </div>
                                    <div className="ccf-grid">
                                        {renderField({
                                            name: 'contactPersonName',
                                            label: 'Contact Person',
                                            placeholder: 'Full name',
                                            required: true
                                        })}
                                        {renderField({
                                            name: 'contactNo',
                                            label: 'Mobile Number',
                                            type: 'tel',
                                            placeholder: '9876543210',
                                            required: true
                                        })}
                                        {renderField({
                                            name: 'email',
                                            label: 'Email Address',
                                            type: 'email',
                                            placeholder: 'name@company.com',
                                            required: true
                                        })}
                                    </div>
                                </section>

                                <section className="ccf-card ccf-fade-in delay-2">
                                    <div className="ccf-section-heading">
                                        <span><MapPin size={20} /></span>
                                        <div>
                                            <h2>Address Information</h2>
                                            <p>Location fields auto-fill when a valid Indian PIN code is entered.</p>
                                        </div>
                                    </div>
                                    <div className="ccf-grid">
                                        <div className="ccf-field">
                                            <label htmlFor="pinCode">PIN Code <span>*</span></label>
                                            <div className="ccf-input-shell ccf-location-input">
                                                <input
                                                    id="pinCode"
                                                    ref={(element) => {
                                                        fieldRefs.current.pinCode = element;
                                                    }}
                                                    type="text"
                                                    inputMode="numeric"
                                                    name="pinCode"
                                                    value={formData.pinCode}
                                                    placeholder="400001"
                                                    maxLength="6"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    onKeyDown={handleKeyDown}
                                                />
                                                {pinStatus === 'fetching' && <Loader2 className="ccf-spin" size={17} />}
                                                {pinStatus === 'success' && <CheckCircle2 className="ccf-valid" size={17} />}
                                            </div>
                                            {errors.pinCode && (
                                                <span className="ccf-error">
                                                    <CircleAlert size={14} />
                                                    {errors.pinCode}
                                                </span>
                                            )}
                                        </div>
                                        {renderField({
                                            name: 'city',
                                            label: 'City',
                                            placeholder: 'Auto-filled',
                                            required: true,
                                            readOnly: true
                                        })}
                                        {renderField({
                                            name: 'state',
                                            label: 'State',
                                            placeholder: 'Auto-filled',
                                            required: true,
                                            readOnly: true
                                        })}
                                        {renderField({
                                            name: 'address',
                                            label: 'Registered Address',
                                            placeholder: 'Office, street, landmark',
                                            required: true,
                                            multiline: true,
                                            className: 'ccf-wide'
                                        })}
                                    </div>
                                </section>

                                <section className="ccf-card ccf-fade-in delay-3">
                                    <div className="ccf-section-heading">
                                        <span><BriefcaseBusiness size={20} /></span>
                                        <div>
                                            <h2>Business Information</h2>
                                            <p>Compliance and financial identifiers used in policy workflows.</p>
                                        </div>
                                    </div>
                                    <div className="ccf-grid">
                                        {renderField({
                                            name: 'pan',
                                            label: 'PAN Number',
                                            placeholder: 'ABCDE1234F'
                                        })}
                                        {renderField({
                                            name: 'gst',
                                            label: 'GST Number',
                                            placeholder: '27ABCDE1234F1Z5'
                                        })}
                                        {renderField({
                                            name: 'income',
                                            label: 'Yearly Income',
                                            type: 'number',
                                            placeholder: 'Annual value'
                                        })}
                                    </div>
                                </section>

                                <section className="ccf-card ccf-fade-in delay-4">
                                    <div className="ccf-section-heading">
                                        <span><BadgeIndianRupee size={20} /></span>
                                        <div>
                                            <h2>Insurance / Policy Information</h2>
                                            <p>Internal notes for policy servicing and conversion context.</p>
                                        </div>
                                    </div>
                                    <div className="ccf-grid">
                                        {renderField({
                                            name: 'note',
                                            label: 'Customer Note',
                                            placeholder: 'Add underwriting notes, preferences, or servicing context',
                                            multiline: true,
                                            className: 'ccf-wide'
                                        })}
                                    </div>
                                </section>

                                <section className="ccf-card ccf-fade-in delay-5">
                                    <div className="ccf-section-heading">
                                        <span><FileText size={20} /></span>
                                        <div>
                                            <h2>Documents</h2>
                                            <p>Stage supporting files for review before moving to policy workflows.</p>
                                        </div>
                                    </div>
                                    <div
                                        className="ccf-upload-zone"
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            handleDocumentSelection(e.dataTransfer.files);
                                        }}
                                    >
                                        <UploadCloud size={34} />
                                        <strong>Drop files here or browse</strong>
                                        <button
                                            type="button"
                                            className="ccf-upload-button"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <UploadCloud size={17} />
                                            Upload Documents
                                        </button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            onChange={(e) => handleDocumentSelection(e.target.files)}
                                            hidden
                                        />
                                    </div>
                                    {uploadProgress > 0 && (
                                        <div className="ccf-upload-progress">
                                            <div style={{ width: `${uploadProgress}%` }} />
                                        </div>
                                    )}
                                    {documents.length > 0 && (
                                        <div className="ccf-document-list">
                                            {documents.map((doc) => (
                                                <div className="ccf-document-pill" key={doc.id}>
                                                    <FileText size={16} />
                                                    <span>{doc.name}</span>
                                                    <small>{Math.max(1, Math.round(doc.size / 1024))} KB</small>
                                                    <button type="button" onClick={() => removeDocument(doc.id)} aria-label={`Remove ${doc.name}`}>
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </section>

                                <div className="ccf-action-bar">
                                    <button type="button" className="ccf-btn secondary" onClick={handleReset}>
                                        <RotateCcw size={18} />
                                        Reset
                                    </button>
                                    <button type="submit" className="ccf-btn primary" disabled={loading}>
                                        {loading ? <Loader2 className="ccf-spin" size={18} /> : <Send size={18} />}
                                        {loading ? 'Saving Client...' : 'Submit Client'}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </main>
            </div>
        </div>
    );
};

export default Client;
